import React, { useState, useEffect } from 'react';
import { Calendar, MapPin, ChevronDown } from 'lucide-react';
import { CongresoPublico } from '../types/congreso.types';
import { INSTITUCION_NOMBRE_COMPLETO } from '../constants';
import { formatearRangoFechas } from '../utils/dateFormatter';

interface HeroSectionProps {
  congreso: CongresoPublico;
}

export const HeroSection: React.FC<HeroSectionProps> = ({ congreso }) => {
  const [timeLeft, setTimeLeft] = useState<{ days: number; hours: number; minutes: number; seconds: number }>({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  useEffect(() => {
    const targetDate = congreso.fechaDesde
      ? new Date(congreso.fechaDesde).getTime()
      : new Date().getTime();

    const interval = setInterval(() => {
      const now = new Date().getTime();
      const difference = targetDate - now;

      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        });
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [congreso.fechaDesde]);

  const textoFechas = formatearRangoFechas(congreso.fechaDesde, congreso.fechaHasta);
  const bannerUrl = congreso.imagenBannerUrl || '/assets/banner-generico.jpg';

  return (
    <section
      id="inicio"
      className="relative min-h-screen pt-32 pb-20 flex flex-col justify-center overflow-hidden bg-cover bg-center"
      style={{
        backgroundImage: `linear-gradient(180deg, rgba(255, 255, 255, 0.88) 0%, rgba(248, 250, 252, 0.96) 100%), url('${bannerUrl}')`
      }}
    >
      {/* Background Decorativo sutil */}
      <div className="absolute inset-0 opacity-15 pointer-events-none bg-[radial-gradient(#00A3E0_1px,transparent_1px)] [background-size:24px_24px]"></div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center flex flex-col items-center">
        {/* Badge Institucional Unificado con link a Google Maps / Sede (Tarea 2 & 4) */}
        <a
          href="#sede"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand-navy hover:bg-slate-900 text-white text-xs sm:text-sm font-semibold tracking-wide mb-8 shadow-sm transition-all hover:scale-105"
          title="Ver ubicación en Paseo Aldrey, Mar del Plata"
        >
          <span className="w-2 h-2 rounded-full bg-brand-cyan animate-pulse"></span>
          <span>{textoFechas} • {congreso.sede || 'Mar del Plata'}</span>
        </a>

        {/* Logo Oficial del Congreso */}
        <div className="mb-6 max-w-[280px] sm:max-w-[340px] drop-shadow-md">
          <img
            src={congreso.imagenLogoUrl || '/assets/logo-oficial-congreso-2026.png'}
            alt={`Logo ${congreso.nombre || 'Congreso Inmobiliario 2026'}`}
            className="w-full h-auto object-contain max-h-48 mx-auto"
            onError={(e) => {
              e.currentTarget.src = '/assets/logo-oficial-congreso-2026.png';
            }}
          />
        </div>

        {/* Título Principal Dinámico */}
        <h1 className="text-4xl sm:text-6xl lg:text-7xl font-black tracking-tight text-brand-navy max-w-4xl mb-4 leading-tight">
          <span className="block">{congreso.nombre || 'Congreso Inmobiliario 2026'}</span>
        </h1>

        <p className="text-xs sm:text-sm font-medium text-slate-500 uppercase tracking-widest mb-8">
          Organizado por <span>{INSTITUCION_NOMBRE_COMPLETO}</span>
        </p>

        {/* Datos Clave: Fecha y Sede (con enlace a Maps / Sede) */}
        <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 text-slate-700 mb-10 max-w-3xl">
          <div className="flex items-center gap-2.5 px-4 py-2.5 rounded-xl bg-white border border-slate-200 shadow-sm">
            <Calendar className="w-5 h-5 text-brand-cyan" />
            <span className="text-sm sm:text-base font-semibold text-brand-navy">{textoFechas}</span>
          </div>
          {congreso.sede && (
            <a
              href="#sede"
              className="flex items-center gap-2.5 px-4 py-2.5 rounded-xl bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 shadow-sm transition-all hover:border-brand-cyan group"
              title="Ver ubicación en el mapa"
            >
              <MapPin className="w-5 h-5 text-brand-cyan group-hover:scale-110 transition-transform" />
              <span className="text-sm sm:text-base font-semibold text-brand-navy">
                {congreso.sede} {congreso.domicilio ? `(${congreso.domicilio})` : ''}
              </span>
            </a>
          )}
        </div>

        {/* Contador Regresivo de Gran Impacto (Tarea 5) */}
        <div className="grid grid-cols-4 gap-3 sm:gap-6 mb-12 w-full max-w-xl">
          {[
            { label: 'Días', value: timeLeft.days },
            { label: 'Horas', value: timeLeft.hours },
            { label: 'Minutos', value: timeLeft.minutes },
            { label: 'Segundos', value: timeLeft.seconds }
          ].map((item, index) => (
            <div
              key={index}
              className="flex flex-col items-center justify-center p-4 sm:p-6 rounded-2xl bg-white border border-slate-200/80 shadow-lg shadow-slate-200/50 backdrop-blur-sm transition-transform duration-300 hover:scale-105"
            >
              <span className="font-heading font-black text-3xl sm:text-5xl text-brand-navy leading-none">
                {String(item.value).padStart(2, '0')}
              </span>
              <span className="text-[10px] sm:text-xs text-slate-500 font-bold uppercase tracking-wider mt-2">
                {item.label}
              </span>
            </div>
          ))}
        </div>

        {/* Acciones Principales de Difusión */}
        <div className="flex flex-col sm:flex-row items-center gap-4 mb-12">
          <a
            href="#agenda"
            className="w-full sm:w-auto px-8 py-4 rounded-xl bg-brand-cyan hover:bg-sky-500 text-white font-heading font-extrabold text-base tracking-wide uppercase transition-all duration-300 shadow-lg shadow-brand-cyan/25 hover:scale-105 flex items-center justify-center gap-2"
          >
            <Calendar className="w-5 h-5" />
            <span>Ver Programa Oficial</span>
          </a>
          <a
            href="#disertantes"
            className="w-full sm:w-auto px-8 py-4 rounded-xl bg-white hover:bg-slate-50 text-brand-navy border border-slate-200 font-heading font-bold text-base transition-all duration-300 hover:border-brand-cyan shadow-sm flex items-center justify-center"
          >
            Conocer Disertantes
          </a>
        </div>

        {/* Scroll Indicator */}
        <a href="#agenda" className="mt-4 text-slate-400 hover:text-brand-cyan transition-colors animate-bounce" aria-label="Desplazar hacia abajo">
          <ChevronDown className="w-6 h-6" />
        </a>
      </div>
    </section>
  );
};
